// CommonJS shared contract: .cjs keeps CRA and Node in agreement.
const safeImageUrl = (value) => typeof value === 'string' && (value === '' || /^\/server\/course-storyboards\/[1-9][0-9]*\/[a-f0-9]{64}\.(png|jpg|webp)$/.test(value));
const SPACE_FIELDS = ['room', 'zones', 'camera', 'lighting', 'route'];
const SCENE_FIELDS = ['title', 'action', 'before', 'after', 'feedback'];
const LAYERS = ['background', 'middle', 'foreground'];
const DEFAULT_CAMERA = { view: 'left', zoom: 1, panX: 0, panY: 0 };

function resolveStage(board, scene, phase = 'before') {
  const stage = scene.stage || {};
  const overrides = stage[phase] || [];
  return {
    camera: { ...DEFAULT_CAMERA, ...stage.camera },
    backgroundImage: scene.backgroundImage !== undefined ? scene.backgroundImage : (board.backgroundImage || ''),
    objects: board.objects.map((o) => ({ ...o, visible: true, scale: 1, rotation: 0, highlight: false,
      ...overrides.find((s) => s.id === o.id), label: o.label })),
    targetId: stage.targetId || '',
  };
}

function updateSceneStage(board, index, patch) {
  return { ...board, scenes: board.scenes.map((s, i) => i === index
    ? { ...s, stage: { ...s.stage, ...patch } } : s) };
}

function removeStoryboardObject(board, id) {
  return { ...board, objects: board.objects.filter((o) => o.id !== id), scenes: board.scenes.map((s) => {
    if (!s.stage) return s;
    return { ...s, stage: { ...s.stage, targetId: s.stage.targetId === id ? '' : s.stage.targetId,
      before: (s.stage.before || []).filter((o) => o.id !== id), after: (s.stage.after || []).filter((o) => o.id !== id) } };
  }) };
}

function createStoryboard(title = '실습') {
  return {
    version: 1,
    room: `${title} 실습실 · 8m × 6m · 뒤쪽 벽과 창문, 왼쪽 출입문, 미끄럼 방지 바닥`,
    zones: '입구 / 준비대 / 중앙 작업대 / 오른쪽 결과 확인 구역',
    camera: '고정 3/4 시점 · 높이 1.6m · 35mm 느낌 · 16:9 · 좌표는 화면 좌상단 기준 0~100%',
    lighting: '왼쪽 창문 자연광 · 오른쪽 약한 보조광 · 오른쪽 아래로 부드러운 그림자',
    route: '입구 → 준비대 → 작업대 → 결과 확인 구역. 통로는 비우고 클릭 경로로 표시.',
    objects: [
      { id: 'room', label: '벽·창문', layer: 'background', x: 50, y: 22 },
      { id: 'station', label: '작업대·장비', layer: 'middle', x: 53, y: 50 },
      { id: 'learner', label: '학습자', layer: 'foreground', x: 28, y: 73 },
      { id: 'tools', label: '도구·소품', layer: 'foreground', x: 73, y: 69 },
    ],
    scenes: ['공간 파악', '준비와 선택', '조작과 피드백', '결과 확인'].map((name, i) => ({
      id: `scene-${i + 1}`, title: name,
      action: ['공간을 둘러보고 목표 확인', '준비대에서 도구 선택', '작업대의 대상을 클릭하거나 드래그', '결과 확인 구역에서 결과 비교'][i],
      before: ['초기 상태', '도구 미선택', '작업 대기', '결과 미확인'][i],
      after: ['목표 표시', '선택 도구 강조', '조작 결과 반영', '완료 표시'][i],
      feedback: '성공 조건과 오류 복구 방법을 구체적으로 편집하세요.',
      stage: { camera: { ...DEFAULT_CAMERA, zoom: [1, 1.1, 1.2, 1][i] }, targetId: 'station',
        before: [{ id: 'learner', x: [18, 30, 44, 68][i], y: 73 }],
        after: [{ id: 'learner', x: [30, 44, 58, 78][i], y: 66 }, { id: 'station', highlight: true }] },
    })),
  };
}

function validateStoryboard(value) {
  if (value == null) return null;
  const text = (s) => typeof s === 'string' && s.length <= 2000;
  if (value.backgroundImage !== undefined && !safeImageUrl(value.backgroundImage)) return '등록한 교육 이미지 URL만 사용할 수 있습니다.';
  if (typeof value !== 'object' || Array.isArray(value) || value.version !== 1) return '스토리보드 버전이 올바르지 않습니다.';
  if (JSON.stringify(value).length > 50000) return '스토리보드는 50,000자 이하여야 합니다.';
  if (!SPACE_FIELDS.every((k) => text(value[k]))) return '공간·카메라·동선 입력을 확인하세요 (각 2,000자 이하).';
  if (!Array.isArray(value.objects) || value.objects.length < 1 || value.objects.length > 20) return '오브젝트는 1~20개여야 합니다.';
  if (!value.objects.every((o) => o && text(o.id) && o.id && text(o.label) && LAYERS.includes(o.layer)
    && ['x', 'y'].every((k) => Number.isFinite(o[k]) && o[k] >= 0 && o[k] <= 100))) return '오브젝트 레이어 또는 좌표(0~100)를 확인하세요.';
  if (new Set(value.objects.map((o) => o.id)).size !== value.objects.length) return '오브젝트 ID가 중복됩니다.';
  if (!Array.isArray(value.scenes) || value.scenes.length !== 4 || !value.scenes.every((s) => s && text(s.id) && s.id && SCENE_FIELDS.every((k) => text(s[k])))) return '4개 장면의 내용을 확인하세요.';
  if (new Set(value.scenes.map((s) => s.id)).size !== 4) return '장면 ID가 중복됩니다.';
  const range = (n, min, max) => Number.isFinite(n) && n >= min && n <= max;
  const ids = new Set(value.objects.map((o) => o.id));
  for (const scene of value.scenes) {
    if (scene.backgroundImage !== undefined && !safeImageUrl(scene.backgroundImage)) return '장면 이미지 URL이 올바르지 않습니다.';
    if (scene.stage === undefined) continue;
    const s = scene.stage;
    if (!s || typeof s !== 'object' || Array.isArray(s)) return '장면 배치 형식이 올바르지 않습니다.';
    if (s.targetId !== undefined && s.targetId !== '' && !ids.has(s.targetId)) return '장면 대상 오브젝트가 없습니다.';
    if (s.camera !== undefined) {
      const c = s.camera;
      if (!c || !['left','right','front'].includes(c.view) || !range(c.zoom,0.6,1.6)
        || !range(c.panX,-80,80) || !range(c.panY,-60,60)) return '장면 카메라 범위를 확인하세요.';
    }
    for (const phase of ['before','after']) {
      if (s[phase] === undefined) continue;
      if (!Array.isArray(s[phase]) || s[phase].length > 20) return '장면 오브젝트는 최대 20개입니다.';
      const seen = new Set();
      for (const o of s[phase]) {
        if (!o || !ids.has(o.id) || seen.has(o.id)) return '장면 오브젝트 ID를 확인하세요.';
        seen.add(o.id);
        if (['x','y'].some((k) => o[k] !== undefined && !range(o[k],0,100))
          || (o.scale !== undefined && !range(o.scale,0.4,2))
          || (o.rotation !== undefined && !range(o.rotation,-180,180))
          || (o.layer !== undefined && !LAYERS.includes(o.layer))
          || ['visible','highlight'].some((k) => o[k] !== undefined && typeof o[k] !== 'boolean')) return '장면 좌표·크기·표시 상태를 확인하세요.';
      }
    }
  }
  return null;
}

function scenePrompt(board, scene) {
  return [
    '2.5D 교육 시뮬레이션용 장면 이미지. 실제 3D 엔진이 아닌 2D 레이어 합성용 시각 자료.',
    `장면: ${scene.title}`, `공간: ${board.room}`, `구역: ${board.zones}`,
    `카메라: ${board.camera}`, `조명: ${board.lighting}`, `동선: ${board.route}`,
    '모든 컷에서 공간 구조·인물 외형·장비 색상·광원을 유지. 공통 카메라는 기본값이며 아래 컷별 시점·확대·이동 명세가 우선한다.',
    ...board.objects.map((o) => `${o.id} / ${o.label}: ${o.layer}, 화면 좌표 x=${o.x}%, y=${o.y}%`),
    ...['before','after'].flatMap((phase) => {
      const frame = resolveStage(board,scene,phase);
      return [`${phase === 'before' ? '행동 전' : '행동 후'} 시각 배치: 시점=${frame.camera.view}, 확대=${frame.camera.zoom}, 이동=${frame.camera.panX}/${frame.camera.panY}`,
        ...frame.objects.map((o) => `${o.id}: x=${o.x}%, y=${o.y}%, ${o.layer}, 크기=${o.scale}, 회전=${o.rotation}, 표시=${o.visible}, 강조=${o.highlight}`)];
    }),
    `상호작용 대상 ID: ${scene.stage?.targetId || '미지정'}`,
    `행동: ${scene.action}`, `이전 상태: ${scene.before}`, `이후 상태: ${scene.after}`, `피드백: ${scene.feedback}`,
    '자산 분리: 배경은 인물·조작 도구 없이, 오브젝트는 같은 시점의 투명 배경 PNG로 각각 별도 생성.',
    '깊이 순서: background → middle → foreground. 접지 그림자는 별도 레이어. 잘린 도구·가려진 조작부 금지.',
    '문자·로고·버튼·HUD·성공 배지를 이미지에 굽지 말 것. 라벨과 클릭 영역은 런타임 UI로 합성.',
    '출력: 장면 전체 참고 이미지 1장 + ID별 자산. 이미지 생성은 별도 제공자에서 실행.',
  ].join('\n');
}

module.exports = { createStoryboard, validateStoryboard, scenePrompt, resolveStage, updateSceneStage, removeStoryboardObject, SPACE_FIELDS, SCENE_FIELDS, LAYERS };
